// TGCLOUD-1818 — query state for Search Family Records: the chips and the
// free-text term, held in one place and mirrored into the URL.
//
// WHY THE URL. Clinical users share links ("is this the family you meant?"),
// and a filtered result set that cannot be linked to is a result set that gets
// described over the phone instead. Retrofitting URL state after the component
// tree has settled is far more invasive than starting with it.
//
// TWO DIFFERENT HISTORY BEHAVIOURS, deliberately:
//
//   chips      -> PUSH. A chip is a discrete decision, so Back should undo
//                 exactly one chip. That is what makes the browser's Back
//                 button a usable "undo" for this screen.
//   free text  -> REPLACE, debounced. Typing is continuous; pushing a history
//                 entry per keystroke would make Back useless (fifty presses
//                 to escape one word) and would re-render the router on every
//                 character. The in-memory value still updates synchronously,
//                 so the box itself behaves exactly as it always has.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isRange, isValueless, toFilterDate } from './filterPredicates';

export const CRITERIA_PARAM = 'filters';
export const SEARCH_PARAM = 'q';

/** How long the free-text box may run ahead of the URL. */
const URL_SYNC_DELAY_MS = 300;

// A range value is two 'YYYY-MM-DD' strings. Neither can contain '..', so this
// needs no escaping of its own — unlike a free-form value, which is
// percent-encoded because encodeURIComponent escapes both ',' and ';'.
const RANGE_JOIN = '..';
const PART_JOIN = ',';
const CRITERION_JOIN = ';';

/**
 * Serialise criteria into one query-parameter value.
 *
 * @param {Array<{field:string,operator:string,value:*}>} criteria
 * @returns {string} '' when there are none
 */
// A Date must never reach the URL. FilterBuilder already canonicalises a date
// criterion on commit, but this is the last gate before the value becomes a
// string for good, and `String(new Date(...))` produces
// "Mon Mar 12 1965 00:00:00 GMT+0000 (…)" — which no parser downstream
// recognises, so the chip survives the round trip while its filter does not.
// Normalising in both places costs nothing and removes the whole class.
const serialisableValue = (value) =>
  value instanceof Date ? toFilterDate(value) : String(value ?? '');

export const serialiseCriteria = (criteria) => {
  if (!Array.isArray(criteria) || !criteria.length) return '';
  return criteria
    .map(({ field, operator, value }) => {
      const parts = [encodeURIComponent(field), encodeURIComponent(operator)];
      if (isValueless(operator)) return parts.join(PART_JOIN);
      if (isRange(operator)) {
        parts.push(
          `${serialisableValue(value?.from)}${RANGE_JOIN}${serialisableValue(
            value?.to
          )}`
        );
      } else {
        parts.push(encodeURIComponent(serialisableValue(value)));
      }
      return parts.join(PART_JOIN);
    })
    .join(CRITERION_JOIN);
};

/**
 * Parse criteria back out of a query-parameter value.
 *
 * Tolerant by design: a malformed entry is DROPPED, not thrown on. A pasted or
 * hand-edited URL is normal traffic here, and a link that renders a broken
 * screen is worse than one that renders fewer chips than its author intended.
 * What must never happen is a malformed entry silently becoming a WIDER result
 * set, which is why an unparseable criterion is discarded along with its URL
 * entry rather than being treated as "match everything".
 *
 * @param {string} raw
 * @returns {Array<{field:string,operator:string,value:*}>}
 */
// decodeURIComponent throws URIError on a lone '%' or a truncated escape, and
// parseCriteria runs inside a useState initialiser and an effect — so an
// unguarded throw takes the whole screen down with a blank page. A pasted or
// hand-trimmed link is normal traffic here.
const safeDecode = (value) => {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
};

export const parseCriteria = (raw) => {
  if (!raw) return [];
  return String(raw)
    .split(CRITERION_JOIN)
    .map((chunk) => {
      const parts = chunk.split(PART_JOIN);
      if (parts.length < 2) return null;
      const field = safeDecode(parts[0] || '');
      const operator = safeDecode(parts[1] || '');
      if (!field || !operator) return null;
      if (isValueless(operator)) return { field, operator };
      const rawValue = parts.slice(2).join(PART_JOIN);
      if (isRange(operator)) {
        const [from = '', to = ''] = rawValue.split(RANGE_JOIN);
        return { field, operator, value: { from, to } };
      }
      const value = safeDecode(rawValue);
      // A value we cannot decode is dropped WITH its criterion rather than
      // substituted — a criterion with a mangled value would filter on the
      // wrong thing while the chip claimed otherwise.
      if (value === null) return null;
      return { field, operator, value };
    })
    .filter(Boolean);
};

/**
 * Own the chips and the free-text term, and keep the URL in step.
 *
 * @returns {{
 *   criteria: Array, searchText: string,
 *   addCriterion: Function, updateCriterion: Function,
 *   removeCriterion: Function, setSearchText: Function, clearAll: Function,
 *   hasQuery: boolean
 * }}
 */
export default function useFamilySearchFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Read once on mount. After that this hook owns the values and writes them
  // outward; re-reading on every render would fight the debounce and make the
  // text box stutter as the URL caught up with it.
  const [criteria, setCriteria] = useState(() =>
    parseCriteria(searchParams.get(CRITERIA_PARAM))
  );
  const [searchText, setSearchText] = useState(
    () => searchParams.get(SEARCH_PARAM) || ''
  );

  // Kept in refs so the writer effects never need searchParams as a dependency
  // — depending on it would re-fire each writer whenever the OTHER one wrote,
  // and the two would trade updates indefinitely.
  const paramsRef = useRef(searchParams);
  paramsRef.current = searchParams;

  // WHAT WE LAST PUT IN THE URL OURSELVES.
  //
  // This is what separates "the user pressed Back" from "our own write came
  // back round". Without it there are two live races, and the second one
  // destroys user input:
  //
  //   1. clearAll() empties both values, but the criteria writer runs before
  //      the debounced text writer, so it publishes a URL that still carries
  //      `q=sarah`. The adopt-from-URL effect then reads that and puts the
  //      search term straight back — Clear all leaves the box full.
  //   2. Type "sara", then add a chip inside the 300ms debounce. The criteria
  //      writer publishes a URL carrying the PREVIOUS `q`, and the adopt
  //      effect rewinds the box to it, eating the half-typed word.
  //
  // So every write records what it wrote, and the adopt effect ignores a URL
  // that matches. Anything else is a real external navigation.
  const lastWrittenRef = useRef({
    filters: searchParams.get(CRITERIA_PARAM) || '',
    q: searchParams.get(SEARCH_PARAM) || ''
  });

  // Current values, for whichever writer is not the one being driven.
  const criteriaRef = useRef(criteria);
  criteriaRef.current = criteria;
  const searchTextRef = useRef(searchText);
  searchTextRef.current = searchText;

  // Writes BOTH params from a full desired state, so neither writer can
  // publish a URL that contradicts the other's in-memory value.
  // setSearchParams is NOT stable in react-router 7: it is memoised on
  // `searchParams`, which changes on every navigation. A writeParams that
  // depended on it therefore changed identity after each write, re-firing both
  // writer effects and pushing a SECOND identical history entry — so the first
  // press of Back appeared to do nothing. Held in a ref, writeParams is stable
  // for the life of the component and each action writes exactly once.
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  const writeParams = useCallback((next, { replace }) => {
    const params = new URLSearchParams(paramsRef.current);
    if (next.filters) params.set(CRITERIA_PARAM, next.filters);
    else params.delete(CRITERIA_PARAM);
    if (next.q) params.set(SEARCH_PARAM, next.q);
    else params.delete(SEARCH_PARAM);
    lastWrittenRef.current = { filters: next.filters, q: next.q };
    setSearchParamsRef.current(params, { replace });
  }, []);

  // Criteria -> URL, immediately, as a history entry.
  const isFirstCriteria = useRef(true);
  useEffect(() => {
    if (isFirstCriteria.current) {
      isFirstCriteria.current = false;
      return;
    }
    const filters = serialiseCriteria(criteria);
    // Adopting a Back/Forward navigation replaces `criteria` with a NEW array
    // even when the content is identical, which re-entered this effect and
    // PUSHED the state we had just navigated to — destroying Forward and making
    // a longer Back chain oscillate between two entries instead of unwinding.
    // Comparing against what the URL already holds is what stops that.
    if (filters === lastWrittenRef.current.filters) return;
    writeParams({ filters, q: searchTextRef.current }, { replace: false });
  }, [criteria, writeParams]);

  // Free text -> URL, debounced, replacing the current entry.
  const isFirstText = useRef(true);
  useEffect(() => {
    if (isFirstText.current) {
      isFirstText.current = false;
      return undefined;
    }
    const id = setTimeout(() => {
      if (searchText === lastWrittenRef.current.q) return;
      writeParams(
        { filters: serialiseCriteria(criteriaRef.current), q: searchText },
        { replace: true }
      );
    }, URL_SYNC_DELAY_MS);
    return () => clearTimeout(id);
  }, [searchText, writeParams]);

  // URL -> state, for a real external navigation only: Back, Forward, or a
  // pasted link. Our own writes are recognised and ignored (see above).
  useEffect(() => {
    const filters = searchParams.get(CRITERIA_PARAM) || '';
    const q = searchParams.get(SEARCH_PARAM) || '';
    const last = lastWrittenRef.current;
    if (filters === last.filters && q === last.q) return;
    lastWrittenRef.current = { filters, q };
    setCriteria(parseCriteria(filters));
    setSearchText(q);
  }, [searchParams]);

  const addCriterion = useCallback((criterion) => {
    setCriteria((current) => [...current, criterion]);
  }, []);

  const updateCriterion = useCallback((index, criterion) => {
    setCriteria((current) =>
      current.map((existing, i) => (i === index ? criterion : existing))
    );
  }, []);

  const removeCriterion = useCallback((index) => {
    setCriteria((current) => current.filter((_, i) => i !== index));
  }, []);

  /**
   * Drop criteria the caller can no longer honour.
   *
   * Pruning the STATE rather than filtering at the point of use is deliberate:
   * the chip row, the predicates and the result line all index into the same
   * array, so filtering in one place and not the others makes a chip's remove
   * button point at the wrong criterion. Removing it here keeps what is shown,
   * what is applied and what is in the URL the same three things.
   */
  const pruneCriteria = useCallback((isUsable) => {
    setCriteria((current) =>
      current.every((c) => isUsable(c)) ? current : current.filter(isUsable)
    );
  }, []);

  const clearAll = useCallback(() => {
    setCriteria([]);
    setSearchText('');
    // Published here rather than left to the two writers: Clear all is one
    // action and must leave one consistent URL behind it.
    writeParams({ filters: '', q: '' }, { replace: false });
  }, [writeParams]);

  const hasQuery = criteria.length > 0 || searchText.trim().length > 0;

  return useMemo(
    () => ({
      criteria,
      searchText,
      addCriterion,
      updateCriterion,
      removeCriterion,
      setSearchText,
      clearAll,
      pruneCriteria,
      hasQuery
    }),
    [
      criteria,
      searchText,
      addCriterion,
      updateCriterion,
      removeCriterion,
      clearAll,
      pruneCriteria,
      hasQuery
    ]
  );
}
